import { Link } from 'react-router-dom'
import InteractiveSimpleCategoryCard from './InteractiveSimpleCategoryCard'
import InteractiveCategoryCard from './InteractiveCategoryCard'

interface CategoryBlockProps {
  title: string
  categoryId: number
  items: any[]
  itemType: 'subcategories' | 'cards' | 'courses'
  className?: string
  isAvailableContent?: boolean
  maxItems?: number
  onCategoryClick?: (categoryId: number) => void
  onSeeAllClick?: (categoryId: number) => void
  onToggleFavorite?: (itemId: string) => void
  favorites?: string[]
  hasSubscription?: boolean
  searchQuery?: string
  onScrollToTop?: () => void
  lockContent?: boolean
  seeAllLink?: string
  useGridLayout?: boolean // Для курсов в гриде (без подписки)
}

const CategoryBlock = ({
  title,
  categoryId,
  items,
  itemType,
  className = 'category-block',
  isAvailableContent = false,
  maxItems,
  onCategoryClick,
  onSeeAllClick,
  onToggleFavorite,
  favorites = [],
  hasSubscription,
  searchQuery = '',
  onScrollToTop,
  lockContent = false,
  seeAllLink,
  useGridLayout = false,
}: CategoryBlockProps) => {
  const isCourses = itemType === 'courses'
  const gridClass = isCourses
    ? (useGridLayout ? 'courses-grid-layout' : 'courses-block-scroll')
    : isAvailableContent
      ? 'available-content-grid'
      : 'categories-grid'

  // Фильтруем курсы по поисковому запросу
  const filteredItems = isCourses && searchQuery?.trim()
    ? items.filter(item => item.base?.name?.toLowerCase().includes(searchQuery.toLowerCase()))
    : items

  // Для курсов разделяем на покупку и подписку
  const buyCourses = isCourses ? filteredItems.filter(item => item.base?.grant_type === 'buy') : []
  const subCourses = isCourses ? filteredItems.filter(item => item.base?.grant_type === 'subscription') : []

  // Если после фильтрации нет курсов - не показываем блок
  const hasCourses = buyCourses.length > 0 || subCourses.length > 0

  // Если это курсы и после фильтрации ничего нет - не рендерим блок
  if (isCourses && !hasCourses) {
    return null
  }

  return (
    <div className={className}>
      <div className="category-block-header">
        <h3>{title}</h3>
        {seeAllLink ? (
          <Link to={seeAllLink} className="see-all-link">
            Все
          </Link>
        ) : !isAvailableContent && !isCourses && (
          <button
            type="button"
            onClick={() => {
              onScrollToTop?.()
              onSeeAllClick?.(categoryId)
            }}
            className="see-all-link"
          >
            Все
          </button>
        )}
      </div>

      {isCourses ? (
        <div className={gridClass}>
          {/* Большие карточки курсов (покупка) */}
          {buyCourses.map(item => {
            const itemId = item.base?.course_id?.toString()
            const hasTrial = (item.base?.trial_lesson_count ?? 0) > 0
            return (
              <InteractiveCategoryCard
                key={itemId}
                item={item}
                showingType="courses"
                className={`category-card ${useGridLayout ? 'course-card-grid-large' : 'course-card-homepage-large'}`}
                showLockBadge={!item.access && !hasTrial}
                showDemoBadge={!item.access && hasTrial}
                searchQuery={searchQuery}
              />
            )
          })}
          {/* Маленькие карточки курсов (подписка) */}
          {subCourses.map(item => {
            const itemId = item.base?.course_id?.toString()
            return (
              <InteractiveCategoryCard
                key={itemId}
                item={item}
                showingType="courses"
                className={`category-card ${useGridLayout ? 'course-card-grid-small' : 'course-card-homepage-small'}`}
                showLockBadge={!item.access}
                searchQuery={searchQuery}
              />
            )
          })}
        </div>
      ) : (
        <div className={gridClass}>
          {items
            .slice(0, maxItems ?? (isAvailableContent ? undefined : 6))
            .map(item => {
              const itemId = (itemType === 'subcategories' ? item.category_id : item.card_id).toString()

              return (
                <InteractiveSimpleCategoryCard
                  key={itemId}
                  item={item}
                  showingType={itemType as 'subcategories' | 'cards'}
                  onCategoryClick={onCategoryClick}
                  onToggleFavorite={
                    itemType === 'cards' ? onToggleFavorite : undefined
                  }
                  isFavorite={
                    itemType === 'cards' ? favorites.includes(itemId) : false
                  }
                  hasSubscription={hasSubscription}
                  searchQuery={searchQuery}
                  onScrollToTop={onScrollToTop}
                  lockContent={lockContent}
                />
              )
            })}
        </div>
      )}

      {items.length === 0 && (
        <div className="empty-state">
          <p>
            {itemType === 'subcategories'
              ? 'Подкатегории не найдены'
              : itemType === 'courses'
                ? 'Курсы не найдены'
                : 'Карточки не найдены'}
          </p>
        </div>
      )}
    </div>
  )
}

export default CategoryBlock
